import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './DragAndDropTable.scss';
import { Button, Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { BiPencil, BiX } from 'react-icons/all';
import { BiPlus } from 'react-icons/bi';
import CreerModifierEtiquette, { EtiquetteSansCategorie } from './CreerModifierEtiquette';
import { useGetQuery } from '../../../Utils/api';
import axios from 'axios';
import CreerOuModifierCategorie from './CreerModifierCategorie';
import { Categorie } from '../../../../../common/src/entities';
import { FiEye, FiEyeOff } from 'react-icons/fi';

// Définition des types pour les catégories et les étiquettes
export type Category = {
    id: string;
    name: string;
    ordre_affichage: string;
    est_public: string;
    description: string;
    type: 'category'; // Ajoutez une propriété type pour les catégories
    etiquettes: {
        id: string;
        name: string;
        ordre_affichage: string;
        est_public: string;
        type: 'etiquette'; // Ajoutez une propriété type pour les étiquettes
    }[];
};
export type GetCategoriesResponse = Category[];

const GestionnaireCategorieEtiquette = () => {
    const [data, setData] = useState<GetCategoriesResponse>([]);
    const [showModalEtiquette, setShowModalEtiquette] = useState(false);
    const [showModalCategorie, setShowModalCategorie] = useState(false);
    const [showModalDeleteItem, setShowModalDeleteItem] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Category>();
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<Category>();
    const [selectedEtiquette, setSelectedEtiquette] = useState<EtiquetteSansCategorie | null>(null);

    const { data: categoriesEtiquettes, isLoading, isError } = useGetQuery<GetCategoriesResponse>('categories/all');

    // Mise à jour de l'état local lorsque les données des catégories sont chargées
    useEffect(() => {
        if (!isLoading && !isError && categoriesEtiquettes) {
            // Trier les catégories par ordre_affichage
            const categoriesTrier = categoriesEtiquettes.sort(
                (a, b) => Number(a.ordre_affichage) - Number(b.ordre_affichage),
            );

            // Trier les étiquettes de chaque catégorie par ordre_affichage
            const dataTrier = categoriesTrier.map((category) => {
                const etiquetteTrier = category.etiquettes.sort(
                    (a, b) => Number(a.ordre_affichage) - Number(b.ordre_affichage),
                );
                return { ...category, etiquettes: etiquetteTrier };
            });

            setData(dataTrier);
        }
    }, [isLoading, isError, categoriesEtiquettes]);

    // Gestionnaire pour ouvrir le modal d'ajout d'étiquette
    const ouvreModalCreationEtiquette = (categoryId: string) => {
        setSelectedCategoryId(categoryId);
        setShowModalEtiquette(true);
    };
    const ouvreModalModificationCategorie = (category: Category) => {
        setSelectedCategory(category);
        setShowModalCategorie(true);
    };
    function ouvreModalModificationEtiquette(etiquette: EtiquetteSansCategorie) {
        setSelectedEtiquette(etiquette);
        setShowModalEtiquette(true);
    }

    const gereSupressionItem = (item) => {
        setItemToDelete(item);
        setShowModalDeleteItem(true);
    };

    // Gestionnaire pour fermer le modal d'ajout d'étiquette
    const fermeModalEtiquette = () => {
        setShowModalEtiquette(false);
    };
    const fermeModalCategorie = () => {
        setShowModalCategorie(false);
    };

    // Cette fonction est appelée lorsque l'utilisateur confirme la suppression
    const gereConfirmationSupression = async () => {
        if (itemToDelete) {
            if (itemToDelete.type == 'category') {
                await deleteCategorie(itemToDelete.id);
            } else if (itemToDelete.type == 'etiquette') {
                let categorie_id = '';
                for (let i = 0; i < data.length; i++) {
                    const etiquetteIndex = data[i].etiquettes.findIndex((item) => item.id === itemToDelete.id);
                    if (etiquetteIndex !== -1) {
                        categorie_id = data[i].id;
                        break;
                    }
                }
                await deleteEtiquette(categorie_id, itemToDelete.id);
            }
        }
        // Après la suppression réussie, rafraîchir les données ou faire autre chose...
        setShowModalDeleteItem(false);
    };

    const gereCreationEtiquette = (newEtiquette) => {
        const newData = [...data];
        const categoryIndex = newData.findIndex((category) => category.id === selectedCategoryId);
        if (categoryIndex !== -1) {
            newData[categoryIndex].etiquettes.push(newEtiquette);
            setData(newData);
        }
        fermeModalEtiquette();
    };

    const updateCategorieEstPublic = async (categorie) => {
        let est_public = categorie.est_public;
        est_public = est_public == 'non' ? 'oui' : 'non';

        try {
            await axios.put(`/api/categories/update-est-public`, { id: categorie.id, est_public });

            // Après avoir mis à jour avec succès la catégorie dans la base de données,
            // mettez également à jour l'état local `data`
            const newData = [...data];
            const categoryIndex = newData.findIndex((category) => category.id === categorie.id);
            if (categoryIndex !== -1) {
                newData[categoryIndex].est_public = est_public;
                setData(newData);
            }
        } catch (error) {
            console.error("Erreur lors de la mise à jour de l'état public de la catégorie", error);
        }
    };

    const updateEtiquetteEstPublic = async (etiquette) => {
        let est_public = etiquette.est_public;
        est_public = est_public == 'non' ? 'oui' : 'non';

        try {
            await axios.put(`/api/etiquettes/update-est-public`, { id: etiquette.id, est_public });

            // Après avoir mis à jour avec succès l'étiquette dans la base de données,
            // mettez également à jour l'état local `data`
            const newData = [...data];
            for (let i = 0; i < newData.length; i++) {
                const etiquetteIndex = newData[i].etiquettes.findIndex((item) => item.id === etiquette.id);
                if (etiquetteIndex !== -1) {
                    newData[i].etiquettes[etiquetteIndex].est_public = est_public;
                    setData(newData);
                    break;
                }
            }
        } catch (error) {
            console.error("Erreur lors de la mise à jour de l'état public de la catégorie", error);
        }
    };

    const updateCategoriesOrder = async (categories) => {
        const updatedCategories = categories.map((category, index) => {
            return { id: category.id, ordre_affichage: index.toString() };
        });

        try {
            await axios.put(`/api/categories/update-ordre-affichage`, updatedCategories);
        } catch (error) {
            console.error("Erreur lors de la mise à jour de l'ordre d'affichage des catégories", error);
        }
    };

    const updateEtiquettesOrder = async (etiquette, newCategorie) => {
        const updatedEtiquettes = etiquette.map((etiquette, index) => {
            return {
                etiquette_id: etiquette.id,
                new_categorie_id: newCategorie,
                ordre_affichage: index.toString(),
            };
        });
        try {
            await axios.put(`/api/etiquettes/update-ordre-affichage`, updatedEtiquettes);
        } catch (error) {
            console.error("Erreur lors de la mise à jour de l'ordre d'affichage des étiquettes", error);
        }
    };

    const deleteCategorie = async (categoryId) => {
        try {
            await axios.delete(`/api/categories/${categoryId}`);

            // mettre également à jour l'état local `data`
            const newData = data.filter((category) => category.id !== categoryId);
            setData(newData);
        } catch (error) {
            console.error('Erreur lors de la suppression de la catégorie', error);
        }
    };

    const deleteEtiquette = async (categoryId, etiquetteId) => {
        try {
            await axios.delete(`/api/etiquettes/${etiquetteId}`);

            // mettre également à jour l'état local `data`
            const newData = [...data];
            const categoryIndex = newData.findIndex((category) => category.id === categoryId);
            if (categoryIndex !== -1) {
                newData[categoryIndex].etiquettes = newData[categoryIndex].etiquettes.filter(
                    (etiquette) => etiquette.id !== etiquetteId,
                );
                setData(newData);
            }
        } catch (error) {
            console.error("Erreur lors de la suppression de l'étiquette", error);
        }
    };

    const gereModificationCategorie = (categorieModifiee: Categorie) => {
        const newData = [...data];

        // Trouvez l'index de la catégorie modifiée dans les données
        const categorieIndex = newData.findIndex((categorie) => Number(categorie.id) === categorieModifiee.id);

        if (categorieIndex !== -1) {
            // Mettez à jour la catégorie modifiée dans les données
            newData[categorieIndex].name = categorieModifiee.name;
            newData[categorieIndex].est_public = categorieModifiee.est_public;
            setData(newData);
        }
    };
    const gereModificationEtiquette = (newEtiquette) => {
        setSelectedEtiquette(null);

        const newData = [...data];

        for (let i = 0; i < newData.length; i++) {
            const etiquetteIndex = newData[i].etiquettes.findIndex(
                (etiquette) => Number(etiquette.id) === newEtiquette.id,
            );

            // Si l'étiquette a été trouvée dans la catégorie actuelle
            if (etiquetteIndex !== -1) {
                // Mettre à jour l'étiquette modifiée dans les données
                newData[i].etiquettes[etiquetteIndex].name = newEtiquette.name;
                newData[i].etiquettes[etiquetteIndex].est_public = newEtiquette.est_public;
                setData(newData);
                break;
            }
        }
    };

    const gereFinDragAndDrop = (result: any) => {
        // Si l'élément n'a pas été déposé dans une destination, on ne fait rien
        if (!result.destination) return;

        // Extraction des informations nécessaires à partir du résultat du glisser-déposer
        const { source, destination, draggableId, type } = result;

        // Si l'élément déplacé est une catégorie
        if (type === 'categorie') {
            // Création d'une nouvelle copie du tableau des catégories
            const categories = Array.from(data);

            // Suppression de la catégorie de son emplacement d'origine
            const [reorderedCategory] = categories.splice(source.index, 1);
            // Insertion de la catégorie à son nouvel emplacement
            categories.splice(destination.index, 0, reorderedCategory);
            // Mettre à jour l'ordre d'affichage pour chaque catégorie dans la bd
            updateCategoriesOrder(categories);

            // Mise à jour de l'état avec le nouveau tableau de catégories
            setData(categories);
        }

        // Si l'élément déplacé est une étiquette
        if (type === 'etiquette') {
            // Extraction des identifiants des catégories source et destination
            const sourceCategoryId = source.droppableId;
            const destinationCategoryId = destination.droppableId;

            // Si l'étiquette est déplacée au sein de la même catégorie
            if (sourceCategoryId === destinationCategoryId) {
                // Création d'une nouvelle copie du tableau des catégories
                const copyCategories = Array.from(data);
                // Trouver l'index de la catégorie source dans le tableau
                const categoryIndex = copyCategories.findIndex((cat) => cat.id.toString() === sourceCategoryId);
                if (categoryIndex !== -1) {
                    // Création d'une nouvelle copie de la catégorie source
                    const category = { ...copyCategories[categoryIndex] };
                    // Création d'une nouvelle copie du tableau des étiquettes de la catégorie
                    const etiquettes = Array.from(category.etiquettes);
                    // Suppression de l'étiquette de son emplacement d'origine
                    const [reorderedEtiquette] = etiquettes.splice(source.index, 1);
                    // Insertion de l'étiquette à son nouvel emplacement
                    etiquettes.splice(destination.index, 0, reorderedEtiquette);
                    // Mise à jour des étiquettes de la catégorie
                    category.etiquettes = etiquettes;
                    // Mise à jour de la catégorie dans le tableau des catégories
                    copyCategories[categoryIndex] = category;

                    // Mettre à jour l'ordre d'affichage pour chaque catégorie dans la bd
                    updateEtiquettesOrder(etiquettes, category);

                    // Mise à jour de l'état avec le nouveau tableau de catégories
                    setData(copyCategories);
                }
            } else {
                // Si l'étiquette est déplacée vers une autre catégorie
                // Création d'une nouvelle copie du tableau des catégories
                const categories = Array.from(data);
                // Trouver les index des catégories source et destination dans le tableau
                const sourceCategoryIndex = categories.findIndex((cat) => cat.id.toString() === sourceCategoryId);
                const destinationCategoryIndex = categories.findIndex(
                    (cat) => cat.id.toString() === destinationCategoryId,
                );

                if (sourceCategoryIndex !== -1 && destinationCategoryIndex !== -1) {
                    // Création de nouvelles copies des catégories source et destination
                    const sourceCategory = { ...categories[sourceCategoryIndex] };
                    const destinationCategory = { ...categories[destinationCategoryIndex] };

                    // Création de nouvelles copies des tableaux des étiquettes des catégories source et destination
                    const sourceEtiquettes = Array.from(sourceCategory.etiquettes);
                    const destinationEtiquettes = Array.from(destinationCategory.etiquettes);

                    // Suppression de l'étiquette de son emplacement d'origine
                    const [movedEtiquette] = sourceEtiquettes.splice(source.index, 1);
                    // Insertion de l'étiquette à son nouvel emplacement dans la catégorie destination
                    destinationEtiquettes.splice(destination.index, 0, movedEtiquette);

                    // Mise à jour des étiquettes des catégories source et destination
                    sourceCategory.etiquettes = sourceEtiquettes;
                    destinationCategory.etiquettes = destinationEtiquettes;

                    // Mise à jour des catégories dans le tableau des catégories
                    categories[sourceCategoryIndex] = sourceCategory;
                    categories[destinationCategoryIndex] = destinationCategory;

                    //updateEtiquettesOrderToAnotherCategorie(destinationEtiquettes, destinationCategory);
                    updateEtiquettesOrder(destinationEtiquettes, destinationCategoryId);
                    // Mise à jour de l'état avec le nouveau tableau de catégories
                    setData(categories);
                }
            }
        }
    };

    return (
        <DragDropContext onDragEnd={gereFinDragAndDrop}>
            <Droppable droppableId="categories" type="categorie">
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`category-container ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                    >
                        {data.map((category, categoryIndex) => (
                            <Draggable
                                draggableId={'cat' + category.id.toString()}
                                index={categoryIndex}
                                key={category.id.toString()}
                                type="categorie"
                            >
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`category ${snapshot.isDragging ? 'dragging' : ''}`}
                                    >
                                        <div className="category-header">
                                            <OverlayTrigger
                                                key={'top'}
                                                placement={'top'}
                                                overlay={<Tooltip id={`tooltip-top`}>{category.description}</Tooltip>}
                                            >
                                                <h5>{category.name}</h5>
                                            </OverlayTrigger>
                                            <div className="category-actions mb-2">
                                                <Button
                                                    variant="link"
                                                    className="mr-2"
                                                    onClick={() => ouvreModalModificationCategorie(category)}
                                                >
                                                    <BiPencil size="1.25em" />
                                                </Button>
                                                <Modal show={showModalCategorie} onHide={fermeModalCategorie}>
                                                    {selectedCategory && (
                                                        <CreerOuModifierCategorie
                                                            modal
                                                            gereModificationCategorie={gereModificationCategorie}
                                                            categorie={selectedCategory}
                                                            estEnModification
                                                        />
                                                    )}
                                                </Modal>

                                                <Button
                                                    variant="link"
                                                    className="mr-2"
                                                    onClick={() => updateCategorieEstPublic(category)}
                                                >
                                                    {category.est_public == 'oui' ? (
                                                        <FiEye size="1.25em" />
                                                    ) : (
                                                        <FiEyeOff size="1.25em" style={{ color: 'gray' }}></FiEyeOff>
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="link"
                                                    className="text-muted mr-2"
                                                    onClick={() =>
                                                        gereSupressionItem({ ...category, type: 'category' })
                                                    }
                                                >
                                                    <BiX size="1.25em" />
                                                </Button>
                                                <Modal
                                                    show={showModalDeleteItem}
                                                    onHide={() => setShowModalDeleteItem(false)}
                                                >
                                                    <Modal.Header closeButton>
                                                        <Modal.Title>Confirmation de suppression</Modal.Title>
                                                    </Modal.Header>
                                                    <Modal.Body>
                                                        Êtes-vous sûr de vouloir supprimer cet item ? Cette action est
                                                        irréversible.
                                                    </Modal.Body>
                                                    <Modal.Footer>
                                                        <Button
                                                            variant="secondary"
                                                            onClick={() => setShowModalDeleteItem(false)}
                                                        >
                                                            Annuler
                                                        </Button>
                                                        <Button variant="danger" onClick={gereConfirmationSupression}>
                                                            Supprimer
                                                        </Button>
                                                    </Modal.Footer>
                                                </Modal>
                                            </div>
                                            <div className="plus-etiquette">
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => ouvreModalCreationEtiquette(category.id)}
                                                >
                                                    <BiPlus /> Ajouter une étiquette
                                                </Button>
                                            </div>
                                        </div>
                                        <Modal show={showModalEtiquette} onHide={fermeModalEtiquette}>
                                            {selectedEtiquette ? (
                                                <CreerModifierEtiquette
                                                    modal
                                                    gereModificationEtiquette={gereModificationEtiquette}
                                                    categorieId={selectedCategoryId}
                                                    etiquette={selectedEtiquette}
                                                    estEnModification
                                                />
                                            ) : (
                                                <CreerModifierEtiquette
                                                    modal
                                                    gereCreationEtiquette={gereCreationEtiquette}
                                                    categorieId={selectedCategoryId}
                                                />
                                            )}
                                        </Modal>

                                        <Droppable droppableId={category.id.toString()} type="etiquette">
                                            {(provided) => (
                                                <Table
                                                    striped
                                                    bordered
                                                    hover
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className="etiquette-table"
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Étiquette</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {category.etiquettes.map((etiquette, etiquetteIndex) => (
                                                            <Draggable
                                                                draggableId={'et' + etiquette.id.toString()}
                                                                index={etiquetteIndex}
                                                                key={etiquette.id.toString()}
                                                                type="etiquette"
                                                            >
                                                                {(provided) => (
                                                                    <tr
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                    >
                                                                        <td>{etiquette.id}</td>
                                                                        <td className="etiquette-cell">
                                                                            {etiquette.name}
                                                                        </td>
                                                                        <td className="action-cell">
                                                                            <Button
                                                                                variant="link"
                                                                                className="mr-2"
                                                                                onClick={() =>
                                                                                    ouvreModalModificationEtiquette({
                                                                                        ...etiquette,
                                                                                        id: Number(etiquette.id),
                                                                                        description: '',
                                                                                    })
                                                                                }
                                                                            >
                                                                                <BiPencil size="1.25em" />
                                                                            </Button>
                                                                            <Button
                                                                                variant="link"
                                                                                className="mr-2"
                                                                                onClick={() =>
                                                                                    updateEtiquetteEstPublic({
                                                                                        ...etiquette,
                                                                                        id: Number(etiquette.id),
                                                                                    })
                                                                                }
                                                                            >
                                                                                {etiquette.est_public == 'oui' ? (
                                                                                    <FiEye size="1.25em" />
                                                                                ) : (
                                                                                    <FiEyeOff
                                                                                        size="1.25em"
                                                                                        style={{ color: 'gray' }}
                                                                                    ></FiEyeOff>
                                                                                )}
                                                                            </Button>
                                                                            <Button
                                                                                variant="link"
                                                                                className="text-muted mr-2"
                                                                                onClick={() =>
                                                                                    gereSupressionItem({
                                                                                        ...etiquette,
                                                                                        id: Number(etiquette.id),
                                                                                        type: 'etiquette',
                                                                                    })
                                                                                }
                                                                            >
                                                                                <BiX size="1.25em" />
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </tbody>
                                                </Table>
                                            )}
                                        </Droppable>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default GestionnaireCategorieEtiquette;
